'use client';

import {
  BodyDefault,
  LabelDefault,
  MetaDefault,
} from '../../components/typography';
import { Checkbox } from '../../components/checkbox';
import { createSelectListCollection, Select } from '../../components/select';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowUpDownIcon } from '@terra/ui-icons';
import { useMemo } from 'react';

type PipelineOption<TPipeline extends string> = {
  label: string;
  value: TPipeline;
};

type PipelineMultiSelectProps<TPipeline extends string> = {
  label: string;
  helperText?: string;
  summary: string;
  options: PipelineOption<TPipeline>[];
  value: TPipeline[];
  onChange: (value: TPipeline[]) => void;
  parseValue: (value: string) => TPipeline | null;
};

export const PipelineMultiSelect = <TPipeline extends string>({
  label,
  helperText,
  summary,
  options,
  value,
  onChange,
  parseValue,
}: PipelineMultiSelectProps<TPipeline>) => {
  const collection = useMemo(
    () => createSelectListCollection({ items: options }),
    [options],
  );
  const allValues = useMemo(
    () => options.map((option) => option.value),
    [options],
  );
  const selectedValues = value.length > 0 ? value : allValues;

  return (
    <VStack alignItems="stretch" gap={1.5} w="full">
      <LabelDefault>{label}</LabelDefault>
      {helperText ? (
        <MetaDefault color="fg.neutral.muted">{helperText}</MetaDefault>
      ) : null}
      <Select.Root
        collection={collection}
        multiple
        value={selectedValues}
        onValueChange={(details) => {
          const parsed = details.value.flatMap((candidate) => {
            const parsedValue = parseValue(candidate);

            return parsedValue ? [parsedValue] : [];
          });

          onChange(parsed.length === allValues.length ? [] : parsed);
        }}
        size="sm"
        positioning={{ sameWidth: true }}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger>
            <BodyDefault truncate>{summary}</BodyDefault>
            <ArrowUpDownIcon />
          </Select.Trigger>
        </Select.Control>
        <Select.Positioner>
          <Select.Content>
            {collection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                <HStack gap={2} flex="1" minW={0} alignItems="center">
                  <Box
                    flexShrink={0}
                    pointerEvents="none"
                    as="span"
                    css={{
                      '& svg': {
                        color: 'unset !important',
                      },
                    }}
                  >
                    <Checkbox
                      checked={selectedValues.some(
                        (pipeline) => pipeline === item.value,
                      )}
                      size="sm"
                    />
                  </Box>
                  <Select.ItemText>{item.label}</Select.ItemText>
                </HStack>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Positioner>
      </Select.Root>
    </VStack>
  );
};
